import { Registry } from '@lightningjs/sdk';
import { ApplicationInstance } from '@lightningjs/sdk/src/Launch';
import { AbstractDeviceIntegration } from 'config/platforms/AbstractDeviceIntegration';
import { DirectionsType } from 'support/keyEventUtils';
import { debounce } from 'support/generalUtils';
import { getSimulatedKeyEventFromMap } from 'support/keyEventUtils';

const SCROLLING_TIMEOUT = 200;
const SWIPE_THRESHOLD = 5;

export abstract class AbstractMouseDeviceIntegration extends AbstractDeviceIntegration {
  private canScroll = true;
  private lastMousePosition: { x: number; y: number } | null = null;
  private mouseMoveEvent = this.onMouseMoveEvent.bind(this);

  protected isSwipeSupported = false; // True to enable local / non-tizen testing
  protected isMouseDown = false;
  protected isSwiping = false;

  abstract setupMouseDetectionHandling(): void;

  protected setupMouseHandling() {
    this.setupMouseDetectionHandling();
    this.setupScrollWheelHandling();
    this.setupMouseDrag();
  }

  private onHoverStateChangedActions: Record<string, (arg: boolean) => void> =
    {};

  private debounceScrolling = debounce(
    this.handleScrollingStopped.bind(this),
    SCROLLING_TIMEOUT,
  );

  private handleScrollingStopped() {
    this.canScroll = true;
  }

  private setupScrollWheelHandling() {
    Registry.addEventListener(document, 'wheel', (event: WheelEvent) => {
      const { deltaY, deltaX } = event;

      if (!this.canScroll) return;
      // TODO: Ignore horizontal scrolling for phase one of Tizen mouse scroll
      if (deltaX !== 0) return;

      this.canScroll = false;
      this.debounceScrolling();

      const keyMap = this.getKeyMapping();
      const keyName = deltaY > 0 ? 'Down' : 'Up';
      const keyEvent = getSimulatedKeyEventFromMap(keyMap, keyName);
      document.dispatchEvent(keyEvent);
    });
  }

  // Note that all mouse events return the Lightning Canvas as its target element (with Inspector off)
  // This results in necessary workarounds to fire off events against the correct components
  protected setupMouseDrag() {
    if (!this.isSwipeSupported) return;

    Registry.addEventListener(document, 'mousedown', (event: MouseEvent) => {
      this.isMouseDown = true;
      this.lastMousePosition = { x: event.clientX, y: event.clientY };

      Registry.addEventListener(document, 'mousemove', this.mouseMoveEvent);
    });

    Registry.addEventListener(document, 'mouseup', (event: MouseEvent) => {
      if (this.isSwiping) {
        // Race condition with Lightning SDK key release.  We want the key release event to fire first
        Registry.setTimeout(() => {
          this.isMouseDown = false;
          this.isSwiping = false;
        }, 0);
      } else {
        this.isMouseDown = false;
      }

      Registry.removeEventListener(document, 'mousemove', this.mouseMoveEvent);
    });
  }

  private onMouseMoveEvent(event: MouseEvent) {
    if (this.isMouseDown && this.lastMousePosition && this.canScroll) {
      const { x, y } = this.lastMousePosition;
      const deltaX = x - event.clientX;
      const deltaY = y - event.clientY;
      let keyName: DirectionsType | null = null;

      if (
        Math.abs(deltaX) > SWIPE_THRESHOLD &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        keyName = deltaX > 0 ? 'Right' : 'Left';
      } else if (Math.abs(deltaY) > SWIPE_THRESHOLD) {
        keyName = deltaY > 0 ? 'Down' : 'Up';
      }

      if (keyName) {
        this.canScroll = false;
        this.debounceScrolling();
        this.isSwiping = true;

        ApplicationInstance?.emit(
          'swipeAction',
          keyName,
          1, // TODO: Add logic for determining strength of swipe
        );

        this.lastMousePosition = { x: event.clientX, y: event.clientY };
      }
    }
  }

  protected cursorVisibilityChange(isVisible: boolean) {
    this.isHovering = isVisible;

    let actions: Array<(arg: boolean) => void> = [];
    actions = Object.values(this.onHoverStateChangedActions);
    actions.forEach(action => action(this.isHovering));
  }

  override addHoverStateChangedAction(
    key: string,
    action: (arg: boolean) => void,
  ) {
    this.onHoverStateChangedActions[key] = action;
  }

  override removeHoverStateChangedAction(key: string) {
    delete this.onHoverStateChangedActions[key];
  }

  override canMouseClick(): boolean {
    // If the user has swiped, we want to suppress the action to avoid awkward user inputs
    return this.isSwipeSupported ? !this.isSwiping : true;
  }
}
